import { defaultUseQueryRefetchOptions } from '@/_shared/constants.ts'
import { CreativeOSFeatures, CreativeOSPages } from '@/_shared/subscriptionPlans.ts'
import AccessControl from '@/components/AccessControl.tsx'
import { OrderFilterSelect } from '@/components/ad-inspiration/OrderFilterSelect.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs/index.tsx'
import { Loader } from '@/components/custom-components/Loader'
import { LockTagSmallNoBg } from '@/components/custom-components/LockTag/index.tsx'
import NotFoundBox from '@/components/custom-components/NotFound/index.tsx'
import TemplatesFilterPopover from '@/components/custom-components/TemplateFilterPopover/index.tsx'
import { ErrorDisplay } from '@/components/error.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { LandingAdGridView } from '@/components/templates/LandingAdGridView'
import { AdFeedFilterOption } from '@/hooks/useFilterFeed.tsx'
import useUserPermissions from '@/hooks/useUserPermissions.tsx'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { z } from 'zod'
import { orderFilter as OrderFilterType, Tags } from '../../../../src/shared/airtable/index.ts'

type SearchParams = {
  Tags?: Tags
  cacheBuster: number
  sideBarOpen?: boolean
  categories?: string
  searchTerm?: string
  brands?: string
  orderFilter?: z.infer<typeof OrderFilterType>
}
export type SelectedTemplateFilters = {
  categories?: string[]
  brands?: string[]
}
export const Route = createFileRoute('/feeds/search/templates/emails')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const cacheBuster: number = typeof search.cacheBuster === 'number' ? search.cacheBuster : Math.random()
    const sideBarOpen: boolean | undefined = typeof search.sideBarOpen === 'boolean' ? search.sideBarOpen : undefined
    const categories: string | undefined = typeof search.categories === 'string' ? search.categories : undefined
    const searchTerm: string | undefined = typeof search.searchTerm === 'string' ? search.searchTerm : undefined
    const brands: string | undefined = typeof search.brands === 'string' ? search.brands : undefined
    const orderFilter = search?.orderFilter as z.infer<typeof OrderFilterType> | undefined
    return {
      cacheBuster: cacheBuster,
      sideBarOpen,
      searchTerm,
      categories,
      brands,
      orderFilter,
    }
  },
})

function All() {
  const {
    orderFilter,
    categories: queryCategories,
    brands: queryBrands,
    searchTerm: querySearchTerm,
  } = Route.useSearch()
  const navigate = useNavigate()

  // Get the template collections to be passed to the filter
  const { data: templateCategories } = trpc.getEmailTemplateCategories.useQuery()
  const [filterOptions, setFilterOptions] = useState<AdFeedFilterOption[]>([])
  const [selectedFilters, setSelectedFilters] = useState<SelectedTemplateFilters>({
    categories: queryCategories ? queryCategories.split(',') : undefined,
    brands: queryBrands ? (queryBrands.split(',') as string[]) : undefined,
  })

  const { data: brands } = trpc.getBrandsV2.useQuery(undefined, defaultUseQueryRefetchOptions)

  const updateQueryString = useCallback(
    (params: { categories?: string; brands?: string; searchTerm?: string }) => {
      const searchParams = new URLSearchParams()

      if (params.categories) searchParams.set('categories', params.categories)
      if (params.brands) searchParams.set('brands', params.brands)

      navigate({
        to: '/feeds/search/templates/emails',
        replace: true,
        search: (old) => {
          return {
            ...old,
            ...params,
            orderFilter,
            cacheBuster: Math.random(),
          }
        },
      })
    },
    [navigate],
  )

  const handleOptionsChange = useCallback(
    (options: AdFeedFilterOption[]) => {
      const selectedCategories: string[] = []
      const selectedBrands: string[] = []

      options.forEach((group) => {
        group.optionItems.forEach((item) => {
          if (item.value) {
            if (group.title === 'Category') selectedCategories.push(item.label)
            if (group.title === 'Brand') selectedBrands.push(`${item.id}`)
          }
        })
      })

      setSelectedFilters({
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        brands: selectedBrands.length > 0 ? selectedBrands : undefined,
      })

      updateQueryString({
        categories: selectedCategories.length > 0 ? selectedCategories.join(',') : undefined,
        brands: selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
        searchTerm: querySearchTerm,
      })
    },
    [updateQueryString],
  )

  // Update AdFilter options based on selectedFilters
  useEffect(() => {
    if (filterOptions.length === 0 || !selectedFilters) return

    const updatedOptions = filterOptions.map((option) => ({
      ...option,
      optionItems: option.optionItems.map((item) => ({
        ...item,
        value:
          (option.title === 'Category' && selectedFilters.categories?.includes(item.label)) ||
          (option.title === 'Brand' && selectedFilters.brands?.includes(item.id ?? '')),
      })),
    }))

    setFilterOptions((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(updatedOptions)) return prev // Prevent unnecessary updates
      return updatedOptions
    })
  }, [filterOptions, selectedFilters])

  useEffect(() => {
    if (templateCategories && templateCategories.length > 0 && brands && brands.length > 0) {
      setFilterOptions((prevOptions) => {
        if (prevOptions.length > 0) return prevOptions // Prevent redundant updates
        return [
          {
            title: 'Category',
            counter: 0,
            optionItems: templateCategories.map((i) => ({
              label: i.category,
              value: false,
            })),
          },
          {
            title: 'Brand',
            counter: 0,
            optionItems: brands.map((i) => ({
              label: i.brandName,
              value: false,
              id: i.brandId,
              logo: i.brandImage ?? undefined,
            })),
          },
        ]
      })
    }
  }, [templateCategories, brands])

  const { currentPlanConfigs } = useUserPermissions()

  const {
    data: filteredTemplatesByCollections,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.searchEmailTemplate.useInfiniteQuery(
    {
      limit: [
        ...currentPlanConfigs
          .map((config) =>
            config.accessiblePages?.[CreativeOSPages.SEARCH_EMAIL_TEMPLATES]?.limit == 'infinity'
              ? 20
              : config.accessiblePages?.[CreativeOSPages.SEARCH_EMAIL_TEMPLATES]?.limit,
          )
          .filter((limit) => limit !== undefined)
          .sort((a, b) => (b as number) - (a as number)),
        20,
      ][0],
      query: querySearchTerm ?? '',
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialCursor: 1,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  const mappedCollections = useMemo(() => {
    return (
      filteredTemplatesByCollections?.pages.flatMap((collections) => {
        return collections.data.flat()
      }) ?? []
    )
  }, [filteredTemplatesByCollections])

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading &&
      filteredTemplatesByCollections &&
      mappedCollections &&
      mappedCollections.length &&
      filteredTemplatesByCollections.pages[filteredTemplatesByCollections.pages.length - 1]?.data.length > 0
    ) {
      fetchNextPage()
    }
  }, [
    inView,
    hasNextPage,
    fetchNextPage,
    filteredTemplatesByCollections,
    isLoading,
    isFetchingNextPage,
    mappedCollections,
  ])

  return (
    <AccessControl page={CreativeOSPages.SEARCH_EMAIL_TEMPLATES}>
      <Stack className="gap-3 lg:gap-6 relative">
        <Stack className="gap-3 lg:gap-8">
          <FeatureTabs
            tabItems={[
              {
                name: 'Ads',
                link: '/feeds/search/templates/ads',
                searchParams: { searchTerm: querySearchTerm },
                featureName: CreativeOSFeatures.SEARCH_AD_TEMPLATE_NAV_BUTTON,
              },
              {
                name: 'Emails',
                link: '/feeds/search/templates/emails',
                searchParams: { searchTerm: querySearchTerm },
                featureName: CreativeOSFeatures.SEARCH_EMAIL_TEMPLATE_NAV_BUTTON,
              },
              {
                name: 'Landers',
                link: '/feeds/search/templates/landing-pages',
                searchParams: { searchTerm: querySearchTerm },

                featureName: CreativeOSFeatures.SEARCH_LANDER_TEMPLATE_NAV_BUTTON,
              },
            ]}
          />
          <Stack className="gap-3 lg:gap-6">
            <div className={'flex justify-between gap-5 items-center'}>
              <Text weight="semibold" size={'xxl'} className="w-fit">
                Your Search Results{' '}
                {filteredTemplatesByCollections?.pages[0]?.totalHits
                  ? `(${filteredTemplatesByCollections?.pages[0].totalHits})`
                  : ''}
                :<span className="opacity-45">“{querySearchTerm}”</span>
              </Text>
              <div className={'hidden gap-2 items-center flex-wrap justify-end'}>
                <div className="lg:hidden">
                  <OrderFilterSelect
                    defaultFilter={orderFilter || 'Random'}
                    options={['Random', 'Recent', 'Popular', 'Oldest']}
                  />
                </div>
              </div>
            </div>
          </Stack>
        </Stack>
        <Stack className="w-full gap-3">
          <Text weight="medium" size={'sm'} className="w-fit hidden">
            Narrow your search using filters:
          </Text>

          <div
            className={`z-10 lg:sticky lg:top-0 lg:bg-themebackground dark:lg:bg-themebackgrounddark  lg:py-2 hidden ${filterOptions && filterOptions.length > 0 ? 'justify-between' : 'justify-end'} items-center`}
          >
            <div>
              {filterOptions && filterOptions.length > 0 && (
                <TemplatesFilterPopover
                  initialOptions={filterOptions}
                  onOptionsChange={handleOptionsChange}
                  placeholder={'Filter Ad Templates'}
                />
              )}
            </div>

            <div className={'hidden lg:flex'}>
              <OrderFilterSelect
                defaultFilter={orderFilter || 'Random'}
                options={['Random', 'Recent', 'Popular', 'Oldest']}
              />
            </div>
          </div>
        </Stack>
        {isLoading && mappedCollections.length < 1 ? (
          <div className="flex justify-center items-center w-full h-[70vh]">
            <Loader />
          </div>
        ) : isError ? (
          <ErrorDisplay />
        ) : mappedCollections.length === 0 ? (
          <NotFoundBox />
        ) : (
          <LandingAdGridView
            Cards={mappedCollections.map((data) => ({
              created: data.Created,
              ID: data['Email ID'].toString(),
              id: data['Email ID'],
              Screenshot: data.imageUrl,
              type: 'email',
              category: data.Category ?? '',
              isSaved: data.isSaved,
              imageUrl: data.imageUrl,
              brandId: data.brandId,
              brandName: data.brandName,
              brandImage: data.brandImage,
            }))}
            HasAccess={true}
            type={'email'}
          />
        )}
        {currentPlanConfigs
          .map((config) => config.accessiblePages?.[CreativeOSPages.SEARCH_EMAIL_TEMPLATES]?.limit)
          .includes('infinity') ? (
          <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
            {isFetchingNextPage && <Loader />}
          </div>
        ) : filteredTemplatesByCollections &&
          filteredTemplatesByCollections.pages.length >=
            [
              ...currentPlanConfigs
                .map((config) =>
                  config.accessiblePages?.[CreativeOSPages.SEARCH_EMAIL_TEMPLATES]?.limit == 'infinity'
                    ? 20
                    : config.accessiblePages?.[CreativeOSPages.SEARCH_EMAIL_TEMPLATES]?.limit,
                )
                .filter((limit) => limit !== undefined)
                .sort((a, b) => (b as number) - (a as number)),
              20,
            ][0] ? (
          <div className="w-full flex justify-center py-5">
            <AccessControl
              feature={CreativeOSFeatures.UPGRADE}
              FeatureLocked={
                <Button variant={'destructivePurple'} className="w-60 items-center gap-2 cursor-pointer opacity-50">
                  <span>Show More</span> <LockTagSmallNoBg />
                </Button>
              }
            />
          </div>
        ) : null}
      </Stack>
    </AccessControl>
  )
}
